import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Sparkles } from 'lucide-react';
import { useGymBillingOptional } from '../../../contexts/GymBillingContext';

export default function GymBillingBanner() {
    const { t } = useTranslation();
    const billingCtx = useGymBillingOptional();
    const billing = billingCtx?.billing;

    if (!billing || billingCtx?.loading) {
        return null;
    }

    const {
        billing_status: status,
        days_left: daysLeft,
        member_count: members,
        max_users: maxUsers,
        members_at_limit: atLimit,
    } = billing;

    if (status === 'complimentary') {
        return null;
    }

    if (
        status === 'active'
        && billing.subscription_cancel_at_period_end
        && billing.subscription_access_until_label
    ) {
        return (
            <div className="mx-4 mt-3 mb-0 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/30 dark:border-amber-600 px-4 py-3 text-sm text-amber-950 dark:text-amber-100">
                <p className="font-semibold">{t('gym.billing.cancellationBannerTitle')}</p>
                <p className="text-xs mt-1 opacity-90">
                    {t('gym.billing.cancellationScheduled', {
                        date: billing.subscription_access_until_label,
                    })}
                </p>
            </div>
        );
    }

    if (status === 'active') {
        return null;
    }

    if (status === 'expired' || billing.trial_expired) {
        return (
            <div className="mx-4 mt-3 rounded-lg border border-red-300 bg-red-50 dark:bg-red-900/30 dark:border-red-700 px-4 py-3 text-sm text-red-900 dark:text-red-100">
                <p className="font-semibold">{t('gym.billing.expiredTitle')}</p>
                <p className="text-xs mt-1">{t('gym.billing.expiredBody')}</p>
                <Link to="/gym/contabilita" className="inline-block mt-2 text-xs font-medium underline">
                    {t('gym.billing.openAccounting')}
                </Link>
            </div>
        );
    }

    if (status === 'trialing') {
        const endDate = billing.trial_ends_at
            ? new Date(billing.trial_ends_at).toLocaleDateString()
            : '';
        const urgent = daysLeft !== null && daysLeft <= 3;

        return (
            <div
                className={`mx-4 mt-3 mb-0 rounded-lg border px-4 py-3 text-sm flex flex-wrap items-center justify-between gap-3 ${
                    urgent
                        ? 'bg-amber-50 border-amber-300 text-amber-950 dark:bg-amber-900/30 dark:border-amber-600 dark:text-amber-100'
                        : 'bg-indigo-50 border-indigo-200 text-indigo-950 dark:bg-indigo-900/30 dark:border-indigo-600 dark:text-indigo-100'
                }`}
            >
                <div className="flex items-start gap-2 min-w-0">
                    {urgent ? <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" /> : <Sparkles className="h-5 w-5 shrink-0 mt-0.5" />}
                    <div>
                        <p className="font-semibold">{t('gym.billing.trialBannerTitle')}</p>
                        <p className="text-xs opacity-90 mt-0.5">
                            {t('gym.billing.trialBannerBody', {
                                date: endDate,
                                days: daysLeft ?? 0,
                                members,
                                max: maxUsers,
                            })}
                            {atLimit ? ` ${t('gym.billing.trialMemberLimitReached')}` : ''}
                            {' '}
                            {t('gym.billing.trialBlurHint')}
                        </p>
                    </div>
                </div>
                <Link
                    to="/gym/contabilita"
                    className="shrink-0 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 text-xs font-medium"
                >
                    {t('gym.billing.openAccounting')}
                </Link>
            </div>
        );
    }

    return null;
}
